import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  AlertCircle,
  Building2,
  CheckCircle,
  Eye,
  EyeOff,
  Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import Modal, { ModalHeader, ModalBody } from './Modal';
import Button from './Button';
import Input from './Input';
import { integrationsApi } from '../../services/integrations-api';
import { handleApiError } from '../../services/api';
import { buildDefaultConnectionName } from '../../utils/connectionName';
import { PlaidLinkSetup } from '../integrations/PlaidLinkSetup';
import { MySQLConnectionSetup } from '../integrations/MySQLConnectionSetup';
import { track } from '@/utils/analytics';
import type {
  BusinessIntegration,
  ConnectionSetupData,
  ConnectionTestResult,
  IntegrationField
} from '../../types';

interface IntegrationSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: BusinessIntegration;
  /** Business the new source is filed under — explicit, never ambient (R9).
   *  Null until the user picks one in a multi-business workspace. */
  organizationId: string | null;
  organizationName: string | null;
  /** True when the workspace has >1 business, so the destination is worth showing. */
  hasOrgChoice?: boolean;
  onSuccess?: (dataSourceId: string) => void;
}

const IntegrationSetupModal: React.FC<IntegrationSetupModalProps> = ({
  isOpen,
  onClose,
  integration,
  organizationId: organizationId_,
  organizationName,
  hasOrgChoice = false,
  onSuccess,
}) => {

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionName, setConnectionName] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      const initialData: Record<string, string> = {};
      integration.setupFields.forEach(field => {
        initialData[field.key] = field.options?.[0]?.value || '';
      });
      setFormData(initialData);
      setTestResult(null);
      setConnectionName(buildDefaultConnectionName(integration.name, organizationName));
      // Paired with the backend's `source_connected`: the gap between them is
      // the API-token wall.
      track('source_connect_started', { provider: integration.type });
    }
  }, [isOpen, integration, organizationName]);

  // Connect integration mutation
  const connectMutation = useMutation({
    mutationFn: (setupData: ConnectionSetupData) => 
      integrationsApi.connectIntegration(integration.type, setupData),
    onSuccess: (data) => {
      toast.success(`Successfully connected ${integration.name}!`);
      onSuccess?.(data.dataSource.id);
      onClose();
    },
    onError: (error) => {
      toast.error(handleApiError(error) || `Failed to connect ${integration.name}`);
    },
  });

  const handleInputChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    // Clear test result when credentials change
    if (testResult) {
      setTestResult(null);
    }
  };

  const togglePasswordVisibility = (key: string) => {
    setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const validateForm = (): boolean => {
    if (!connectionName.trim()) {
      toast.error('Connection name is required');
      return false;
    }

    for (const field of integration.setupFields) {
      if (field.required && (!formData[field.key] || formData[field.key].trim() === '')) {
        toast.error(`${field.label} is required`);
        return false;
      }

      // Validate patterns
      if (field.validation?.pattern && formData[field.key]) {
        const regex = new RegExp(field.validation.pattern);
        if (!regex.test(formData[field.key])) {
          toast.error(`${field.label} format is invalid`);
          return false;
        }
      }

      // Validate lengths
      if (field.validation?.minLength && formData[field.key]?.length < field.validation.minLength) {
        toast.error(`${field.label} must be at least ${field.validation.minLength} characters`);
        return false;
      }
    }
    return true;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) return;

    setIsTestingConnection(true);
    try {
      const result = await integrationsApi.testConnection(integration.type, formData);
      setTestResult(result);
      
      if (result.success) {
        toast.success('Connection test successful!');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      const message = handleApiError(error) || 'Connection test failed';
      toast.error(message);
      setTestResult({
        success: false,
        message,
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleConnect = () => {
    if (!validateForm()) return;

    const setupData: ConnectionSetupData = {
      name: connectionName.trim(),
      credentials: formData,
      testConnection: true,
      organizationId: organizationId_ ?? undefined,
    };

    connectMutation.mutate(setupData);
  };

  const handlePlaidSuccess = (dataSourceId: string) => {
    toast.success(`Successfully connected ${integration.name}!`);
    onSuccess?.(dataSourceId);
    onClose();
  };

  const renderField = (field: IntegrationField) => {
    const value = formData[field.key] || '';

    switch (field.type) {
      case 'select':
        return (
          <div key={field.key} className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </label>
            {/* Mirrors the shared <Input> classes — a native select inherits no
                background of its own, so without bg-card/text-foreground it
                renders light text on the browser's white default in dark mode.
                The <option> colors are set too: several browsers fall back to
                OS colors for the open dropdown rather than the control's. */}
            <select
              value={value}
              onChange={(e) => handleInputChange(field.key, e.target.value)}
              className="block w-full rounded-lg border border-input px-3 py-2 text-sm transition-colors text-foreground bg-card focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
              required={field.required}
            >
              <option value="" className="bg-card text-foreground">
                Select {field.label}
              </option>
              {field.options?.map(option => (
                <option key={option.value} value={option.value} className="bg-card text-foreground">
                  {option.label}
                </option>
              ))}
            </select>
            {field.description && (
              <p className="text-sm text-muted-foreground">
                {field.description}
              </p>
            )}
          </div>
        );

      case 'password':
        return (
          <div key={field.key} className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <div className="relative">
              <Input
                type={showPasswords[field.key] ? 'text' : 'password'}
                value={value}
                onChange={(e) => handleInputChange(field.key, e.target.value)}
                placeholder={field.placeholder}
                required={field.required}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility(field.key)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
              >
                {showPasswords[field.key] ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {field.description && (
              <p className="text-sm text-muted-foreground">
                {field.description}
              </p>
            )}
          </div>
        );

      default:
        return (
          <div key={field.key} className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <Input
              type={field.type}
              value={value}
              onChange={(e) => handleInputChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
            />
            {field.description && (
              <p className="text-sm text-muted-foreground">
                {field.description}
              </p>
            )}
          </div>
        );
    }
  };

  // The chosen business, surfaced wherever the user is about to commit.
  const targetOrgNotice =
    hasOrgChoice && organizationName ? (
      <div className="flex items-center gap-2 mb-5 p-3 rounded-lg bg-muted border border-border">
        <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <span className="text-sm text-muted-foreground">
          Adding to <span className="font-medium text-foreground">{organizationName}</span>
        </span>
      </div>
    ) : null;

  // The two full-page wizards below hard-navigate, so React state is lost —
  // the destination business has to travel in the URL.
  const orgQuery = organizationId_ ? `?orgId=${encodeURIComponent(organizationId_)}` : '';

  // Check if integration uses custom setup component
  if (integration.customSetupComponent === 'PlaidLinkSetup') {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalHeader onClose={onClose}>
          Connect {integration.name}
        </ModalHeader>
        <ModalBody>
          {targetOrgNotice}
          <PlaidLinkSetup
            organizationId={organizationId_}
            onSuccess={handlePlaidSuccess}
            onCancel={onClose}
          />
        </ModalBody>
      </Modal>
    );
  }

  if (integration.customSetupComponent === 'MySQLConnectionSetup') {
    const handleMySQLSuccess = (dataSourceId: string) => {
      onSuccess?.(dataSourceId);
      onClose();
    };
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalHeader onClose={onClose}>
          Connect {integration.name}
        </ModalHeader>
        <ModalBody>
          {targetOrgNotice}
          <MySQLConnectionSetup
            defaultName={buildDefaultConnectionName(integration.name, organizationName)}
            organizationId={organizationId_}
            onSuccess={handleMySQLSuccess}
            onCancel={onClose}
          />
        </ModalBody>
      </Modal>
    );
  }

  // Webhook setup uses full-page wizard instead of modal
  if (integration.customSetupComponent === 'WebhookSetup') {
    if (isOpen) {
      // Redirect to webhook setup page
      window.location.href = `/integrations/webhook/setup${orgQuery}`;
    }
    return null;
  }

  // File feeds also use a full-page flow instead of a modal
  if (integration.customSetupComponent === 'InboundFileSetup') {
    if (isOpen) {
      window.location.href = `/integrations/inbound-file/setup${orgQuery}`;
    }
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader onClose={onClose}>
        Connect {integration.name}
      </ModalHeader>
      <ModalBody>
        {targetOrgNotice}
        <div className="space-y-6">
        {/* Integration Info */}
        <div className="bg-info/15 border border-info/20 rounded-lg p-4">
          <div className="flex items-start">
            <Info className="w-5 h-5 text-info mt-0.5 mr-3" />
            <div>
              <h4 className="font-medium text-info mb-1">
                Connecting {integration.name}
              </h4>
              <p className="text-sm text-info">
                {integration.description}
              </p>
            </div>
          </div>
        </div>

        {/* Connection Name */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            Connection Name
            <span className="text-destructive ml-1">*</span>
          </label>
          <Input
            type="text"
            value={connectionName}
            onChange={(e) => setConnectionName(e.target.value)}
            placeholder="Enter a name for this connection"
            required
          />
          <p className="text-sm text-muted-foreground">
            Give this connection a descriptive name
          </p>
        </div>

        {/* Setup Fields */}
        <div className="space-y-4">
          {integration.setupFields.map(renderField)}
        </div>

        {/* Test Connection Result */}
        {testResult && (
          <div className={`border rounded-lg p-4 ${
            testResult.success 
              ? 'bg-success/15 border-success/20' 
              : 'bg-destructive/15 border-destructive/20'
          }`}>
            <div className="flex items-start">
              {testResult.success ? (
                <CheckCircle className="w-5 h-5 text-success mt-0.5 mr-3" />
              ) : (
                <AlertCircle className="w-5 h-5 text-destructive mt-0.5 mr-3" />
              )}
              <div>
                <h4 className={`font-medium mb-1 ${
                  testResult.success 
                    ? 'text-success' 
                    : 'text-destructive'
                }`}>
                  {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                </h4>
                <p className={`text-sm ${
                  testResult.success 
                    ? 'text-success' 
                    : 'text-destructive'
                }`}>
                  {testResult.message}
                </p>
                {testResult.details && (
                  <div className="mt-2 text-sm">
                    {testResult.details.merchantName && (
                      <p>Merchant: {testResult.details.merchantName}</p>
                    )}
                    {testResult.details.accountCount && (
                      <p>Accounts found: {testResult.details.accountCount}</p>
                    )}
                    {testResult.details.recentOrdersCount && (
                      <p>Recent orders: {testResult.details.recentOrdersCount}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={connectMutation.isPending}
          >
            Cancel
          </Button>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="secondary"
              onClick={handleTestConnection}
              disabled={isTestingConnection || connectMutation.isPending}
              isLoading={isTestingConnection}
            >
              Test Connection
            </Button>
            
            <Button
              onClick={handleConnect}
              disabled={!connectionName.trim() || connectMutation.isPending}
              isLoading={connectMutation.isPending}
            >
              Connect
            </Button>
          </div>
        </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default IntegrationSetupModal; 